import { useState, useCallback } from 'react';
import { mediaService } from '../../../../services/mediaService';
import { sanitizeFile } from '../../../../utils/fileUtils';
import { FILE_UPLOAD } from '../../../../constants/staticTexts';

export const useFileUpload = ({ onError, allowedTypes, maxFiles, messagePrice, postType = 'POST', context = 'post', uploadLimits }) => {
  const [uploading, setUploading] = useState(false);
  
  // Default upload limits (used if uploadLimits not provided)
  // User: 10MB image, 307MB video | Creator: 20MB image, 10240MB video
  const defaultLimits = {
    maxImageSizeBytes: 10 * 1024 * 1024,      // 10MB
    maxVideoSizeBytes: 307 * 1024 * 1024,     // 307MB
    maxImagesPerPost: 10,
    maxVideosPerPost: 10
  };
  
  // Use provided uploadLimits or fall back to defaults
  const limits = uploadLimits || defaultLimits;

  const getMediaType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'voice';
    return 'file';
  };

  const uploadFile = useCallback(async (mediaItem, updateProgress) => {
    try {
      // Start upload with initial progress
      updateProgress(mediaItem.id, { progress: 0, uploading: true });
      
      // Small delay to ensure progress UI is visible
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use combined upload and process method for posts/messages
      // This uploads to S3 via presigned URL, then Lambda processes for HEIC conversion & thumbnails
      const result = await mediaService.uploadAndProcessMedia(
        mediaItem.file,
        context, // 'post', 'message', 'profile', 'banner'
        (percentComplete) => {
          updateProgress(mediaItem.id, { progress: percentComplete });
        }
      );
      
      // Determine the best preview URL:
      // 1. Use server-generated thumbnail if available
      // 2. For videos, use first of multiple preview frames
      // 3. Fall back to local blob if no thumbnail
      const previewUrl = result.thumbnailUrl || mediaItem.preview;
      
      // Update the media item with the uploaded file data
      updateProgress(mediaItem.id, {
        progress: 100,
        uploading: false,
        // Store s3Key for use when creating post/message
        // Use processedS3Key if file was converted (e.g., HEIC→JPEG)
        s3Key: result.processedS3Key || result.s3Key,
        originalS3Key: result.s3Key,
        uploadId: result.uploadId,
        // Use server thumbnail for preview
        url: previewUrl,
        fileUrl: previewUrl,
        displayUrl: previewUrl,
        thumbnailUrl: result.thumbnailUrl || mediaItem.preview,
        thumbnailS3Key: result.thumbnailS3Key,
        // All thumbnails (for video preview selection)
        thumbnails: result.thumbnails || [],
        selectedThumbnailIndex: 0, // Default to first thumbnail
        // Keep original file for fallback
        file: mediaItem.file,
        // Track media metadata
        mediaType: result.mediaType,
        width: result.width,
        height: result.height,
        duration: result.duration,
        converted: result.converted,
        // Mark as uploaded to S3 and processed
        uploadedToS3: true,
        processed: true
      });
      
    } catch (error) {
      console.error('Upload failed for', mediaItem.fileName, ':', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      let errorMessage = error.message || 'Upload failed';
      
      // Handle specific error cases
      if (error.response?.status === 413 || error.message?.includes('too large')) {
        const fileSize = mediaItem.file.size;
        let sizeDisplay;
        if (fileSize >= 1024 * 1024 * 1024) {
          const sizeGB = Math.round(fileSize / (1024 * 1024 * 1024) * 100) / 100;
          sizeDisplay = `${sizeGB}GB`;
        } else {
          const sizeMB = Math.round(fileSize / (1024 * 1024));
          sizeDisplay = `${sizeMB}MB`;
        }
        errorMessage = `${FILE_UPLOAD.FILE_TOO_LARGE_PREFIX} ${mediaItem.fileName} (${sizeDisplay}). ${FILE_UPLOAD.FILE_TOO_LARGE_SUFFIX}`;
      } else if (error.response?.status === 415 || error.message?.includes('Content type')) {
        errorMessage = `${FILE_UPLOAD.UNSUPPORTED_FILE_TYPE} ${mediaItem.fileName}`;
      } else if (error.response?.status === 401) {
        errorMessage = FILE_UPLOAD.AUTH_REQUIRED;
      } else if (error.response?.status === 403) {
        errorMessage = FILE_UPLOAD.PERMISSION_DENIED;
      } else if (error.response?.status >= 500 || error.message?.includes('S3 upload failed')) {
        errorMessage = FILE_UPLOAD.UPLOAD_FAILED;
      } else if (error.message?.includes('HEIC') || error.message?.includes('conversion')) {
        errorMessage = FILE_UPLOAD.IMAGE_PROCESS_FAILED;
      }

      updateProgress(mediaItem.id, {
        uploading: false,
        error: errorMessage
      });
      onError?.(errorMessage);
    }
  }, [onError, context]);

  const validateFiles = useCallback((files, currentCount) => {
    // Define allowed video formats (matching backend)
    const allowedVideoTypes = [
      'video/mp4',           // MP4
      'video/quicktime',     // MOV  
      'video/webm',          // WebM
      'video/x-msvideo',     // AVI
      'video/x-flv',         // FLV
      'video/x-ms-wmv',      // WMV
      'video/x-matroska',    // MKV
      'video/3gpp',          // 3GP
      'video/3gpp2',         // 3G2
      'video/hevc',          // HEVC
      'video/ogg'            // OGG
    ];
    
    // Image formats that are convertible on the backend
    const convertibleImageTypes = [
      'image/heic',
      'image/heif',
      'image/tiff',
      'image/bmp',
      'image/webp',
      'image/avif'
    ];

    const validFiles = files.filter(file => {
      // Check if it's a convertible HEIC/HEIF file (iPhone photos)
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const isHeicFile = ['heic', 'heif'].includes(fileExtension) || 
                          convertibleImageTypes.slice(0, 2).includes(file.type);
      
      // Basic file type validation with specific video format restrictions
      const isValidType = allowedTypes.some(type => {
        if (type === 'video/*') {
          // For video/*, only allow specific formats
          return allowedVideoTypes.includes(file.type);
        } else if (type === 'image/*') {
          // For image/*, allow standard types + convertible types (HEIC, etc.)
          return file.type.startsWith('image/') || 
                 convertibleImageTypes.includes(file.type) ||
                 isHeicFile;
        } else {
          // For other types (audio/*), use category matching
          const [category] = type.split('/');
          return file.type.startsWith(category);
        }
      });
      
      if (!isValidType) {
        if (file.type.startsWith('video/')) {
          onError?.(`${FILE_UPLOAD.VIDEO_FORMAT_NOT_SUPPORTED} ${file.name}. ${FILE_UPLOAD.VIDEO_SUPPORTED_FORMATS}`);
        } else {
          onError?.(`${FILE_UPLOAD.INVALID_FILE_TYPE} ${file.name}`);
        }
        return false;
      }

      // Check file size based on media type and user limits
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? limits.maxVideoSizeBytes : limits.maxImageSizeBytes;
      const maxSizeDisplay = isVideo 
        ? (limits.maxVideoSizeBytes >= 1024 * 1024 * 1024 
            ? `${Math.round(limits.maxVideoSizeBytes / (1024 * 1024 * 1024) * 10) / 10}GB`
            : `${Math.round(limits.maxVideoSizeBytes / (1024 * 1024))}MB`)
        : `${Math.round(limits.maxImageSizeBytes / (1024 * 1024))}MB`;
      
      if (file.size > maxSize) {
        let sizeDisplay;
        if (file.size >= 1024 * 1024 * 1024) {
          sizeDisplay = `${Math.round(file.size / (1024 * 1024 * 1024) * 100) / 100}GB`;
        } else {
          sizeDisplay = `${Math.round(file.size / (1024 * 1024))}MB`;
        }
        // Give a more helpful message for large videos — include duration/compression tip
        if (isVideo) {
          onError?.(`${FILE_UPLOAD.FILE_TOO_LARGE_PREFIX} ${file.name} (${sizeDisplay}). Maximum allowed: ${maxSizeDisplay}. ${FILE_UPLOAD.VIDEO_TOO_LARGE_TIP}`);
        } else {
          onError?.(`${FILE_UPLOAD.FILE_TOO_LARGE_PREFIX} ${file.name} (${sizeDisplay}). Maximum allowed: ${maxSizeDisplay}`);
        }
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return [];
    
    if (currentCount + validFiles.length > maxFiles) {
      onError?.(`${FILE_UPLOAD.MAX_FILES_PREFIX} ${maxFiles} ${FILE_UPLOAD.MAX_FILES_SUFFIX}`);
      return [];
    }

    return validFiles;
  }, [allowedTypes, onError, maxFiles]);

  const processFiles = useCallback(async (files, updateItems) => {
    const newMediaItems = [];
    
    for (const file of files) {
      // Sanitize the filename before processing
      const sanitizedFile = sanitizeFile(file);
      const preview = URL.createObjectURL(sanitizedFile);
      const mediaType = getMediaType(sanitizedFile.type);
      
      // Apply the 8 post type rules for new media items
      const shouldBeFreePerview = () => {
        // Rule 1: Post without price - all content open
        if (postType === 'POST' && messagePrice <= 0) return true;
        
        // Rule 3: Subscribers only without price - all content open to subscribers
        if (postType === 'SUBSCRIBERS_ONLY' && messagePrice <= 0) return true;
        
        // Rule 5: Followers only without price - all content open to followers & subscribers
        if (postType === 'FOLLOWERS_ONLY' && messagePrice <= 0) return true;
        
        // Rule 7: Target Goal - media shown normally (free) until goal reached
        if (postType === 'TARGET') return true;
        
        // Rule 8: Poll - show media normally with poll
        if (postType === 'POLL') return true;
        
        // Rules 2, 4, 6: Posts with price default to locked (user can toggle)
        return messagePrice <= 0;
      };

      const mediaItem = {
        id: Date.now() + Math.random(),
        file: sanitizedFile, // Use the sanitized file
        originalFileName: file.name, // Keep the original name for reference
        fileName: sanitizedFile.name, // Display the sanitized name
        mimeType: sanitizedFile.type,
        mediaType,
        preview,
        isFreePreview: shouldBeFreePerview(),
        progress: 0,
        uploading: true,
        converted: false, // Backend handles conversion
        conversionStatus: null, // Backend handles conversion
        // For posts, keep the original file and generate preview URL
        url: preview, // Use the blob URL for preview
        thumbnailUrl: preview // Use the blob URL for thumbnail
      };
      
      newMediaItems.push(mediaItem);
    }
    
    updateItems(newMediaItems);
    
    // Start uploads
    for (const item of newMediaItems) {
      await uploadFile(item, (id, updates) => {
        updateItems(prev => prev.map(prevItem => 
          prevItem.id === id ? { ...prevItem, ...updates } : prevItem
        ));
      });
    }
  }, [uploadFile, messagePrice, postType, onError]);

  const handleFiles = useCallback(async (files, currentItems, setMediaItems) => {
    const validFiles = validateFiles(files, currentItems.length);
    if (validFiles.length === 0) return [];

    setUploading(true);
    
    try {
      await processFiles(validFiles, (newItems) => {
        if (Array.isArray(newItems)) {
          setMediaItems(prev => {
            // Determine default isFreePreview for brand-new uploads only
            const newItemDefault = () => {
              // Rule 1: Post without price - all content open
              if (postType === 'POST' && messagePrice <= 0) return true;
              
              // Rule 3: Subscribers only without price - all content open to subscribers
              if (postType === 'SUBSCRIBERS_ONLY' && messagePrice <= 0) return true;
              
              // Rule 5: Followers only without price - all content open to followers & subscribers
              if (postType === 'FOLLOWERS_ONLY' && messagePrice <= 0) return true;
              
              // Rule 7: Target Goal - media shown normally (free) until goal reached
              if (postType === 'TARGET') return true;
              
              // Rule 8: Poll - show media normally with poll
              if (postType === 'POLL') return true;
              
              // Rules 2, 4, 6: Posts with price default to locked (user can toggle)
              return messagePrice <= 0;
            };

            const newItemIds = new Set(newItems.map(i => i.id));
            
            // Preserve isFreePreview on existing items; apply default only to new ones
            return [...prev, ...newItems].map((item) => ({
              ...item,
              isFreePreview: newItemIds.has(item.id) ? newItemDefault() : item.isFreePreview
            }));
          });
        } else {
          setMediaItems(newItems);
        }
      });
      
      return validFiles;
    } finally {
      setUploading(false);
    }
  }, [validateFiles, processFiles, messagePrice, postType]);

  return {
    uploading,
    handleFiles,
    setUploading
  };
};
